import { jsFetch } from '@scalar/snippetz/plugins/js/fetch';
import { pythonRequests } from '@scalar/snippetz/plugins/python/requests';
import { shellCurl } from '@scalar/snippetz/plugins/shell/curl';

export const API_BASE = 'https://api.videocaptioner.cn';
export const KEY_STORAGE = 'videocaptioner-docs-api-key';

export function humanizeApiError(rawMessage = '', status = 0) {
  const raw = String(rawMessage || '请求失败');
  const lower = raw.toLowerCase();
  const requestId = raw.match(/request id:\s*([^\s)]+)/i)?.[1];
  let message = '调用没有成功，请稍后重试。';
  if (status === 401 || lower.includes('invalid token') || lower.includes('unauthorized')) message = '这个 API Key 无效或已经失效，请检查后重新粘贴。';
  else if (status === 429 || lower.includes('load is saturated') || lower.includes('负载已饱和')) message = '这个模型当前通道繁忙，请稍后重试，或换一个模型。';
  else if (lower.includes('no permission') || lower.includes('没有权限')) message = '当前 API Key 没有这个模型的调用权限，请检查令牌的模型限制和分组。';
  else if (status === 503 || lower.includes('无可用渠道') || lower.includes('no available channel') || lower.includes('distributor')) message = '当前令牌分组没有可用通道，请更换分组或选择其他模型。';
  else if (status === 400) message = '请求参数不被这个模型接受，请检查必填参数后重试。';
  return { message, detail: requestId ? `请求 ID：${requestId}` : '' };
}

const MAINTAINED_EXAMPLES = {
  'GET /v1/models': null,
  'POST /v1/chat/completions': {
    model: 'gpt-5.6-luna',
    messages: [
      { role: 'system', content: '你是一个简洁、准确的中文助手。' },
      { role: 'user', content: '用一句话介绍 VideoCaptioner API。' },
    ],
    max_completion_tokens: 256,
    stream: false,
  },
  'POST /v1/responses': {
    model: 'gpt-5.6-terra',
    instructions: '请使用简洁、准确的中文回答。',
    input: '用一句话介绍 VideoCaptioner API。',
    max_output_tokens: 256,
    stream: false,
  },
  'POST /v1/images/generations': {
    model: 'gpt-image-2',
    prompt: '一只戴着耳机的橙色猫咪，白色背景，现代插画风格',
    n: 1,
    size: '1024x1024',
  },
  'POST /v1beta/models/gemini-3.6-flash:generateContent': {
    systemInstruction: { parts: [{ text: '请使用简洁、准确的中文回答。' }] },
    contents: [{ role: 'user', parts: [{ text: '用一句话介绍 VideoCaptioner API。' }] }],
    generationConfig: { maxOutputTokens: 128, temperature: 0.7 },
  },
  'POST /v1beta/models/*path': {
    systemInstruction: { parts: [{ text: '请使用简洁、准确的中文回答。' }] },
    contents: [{ role: 'user', parts: [{ text: '用一句话介绍 VideoCaptioner API。' }] }],
    generationConfig: { maxOutputTokens: 128, temperature: 0.7 },
  },
  'POST /mj/submit/imagine': {
    prompt: '一艘蓝色帆船，简洁现代插画，白色背景 --ar 1:1 --v 8.1',
    botType: 'MID_JOURNEY',
    base64Array: [],
    notifyHook: '',
    state: 'docs-playground',
  },
  'anthropic POST /v1/messages': {
    model: 'claude-sonnet-5',
    max_tokens: 256,
    system: '你是一个简洁、准确的中文助手。',
    messages: [{ role: 'user', content: '用一句话介绍 VideoCaptioner API。' }],
  },
};

export function operationKey(operation) {
  return `${operation.method.toUpperCase()} ${operation.path}`;
}

export function requestVariant(operation, contentType = '') {
  const variants = operation.request?.variants || [];
  return variants.find((item) => item.contentType === contentType)
    || variants.find((item) => item.contentType === operation.request?.contentType)
    || variants[0]
    || operation.request
    || null;
}

export function defaultBody(operation, contentType = '') {
  const key = operationKey(operation);
  const scopedKey = `${operation.platform} ${key}`;
  if (Object.prototype.hasOwnProperty.call(MAINTAINED_EXAMPLES, scopedKey)) return MAINTAINED_EXAMPLES[scopedKey];
  if (Object.prototype.hasOwnProperty.call(MAINTAINED_EXAMPLES, key)) return MAINTAINED_EXAMPLES[key];
  return requestVariant(operation, contentType)?.example ?? null;
}

function parameterKey(parameter) {
  return `${parameter.in}:${parameter.name}`;
}

function parameterDefault(parameter) {
  const value = parameter.example ?? parameter.default;
  if (value !== null && value !== undefined) {
    const output = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (/\{\{[^}]+\}\}|YOUR[_ -]?API[_ -]?KEY|<[^>]+>/i.test(output)) return '';
    return output;
  }
  if (parameter.name?.toLowerCase() === 'anthropic-version') return '2023-06-01';
  if (parameter.in === 'path') return parameter.name === 'id' ? 'TASK_ID' : parameter.name.toUpperCase();
  return '';
}

function topLevelFields(variant) {
  return (variant?.fields || []).filter((field) => Number(field.depth || 0) === 0);
}

function acceptsInitialModel(field, model) {
  if (!field?.enum?.length) return true;
  return field.enum.map(String).includes(String(model));
}

export function createRequestDraft(operation, requestedContentType = '', initialModel = '') {
  const contentType = requestedContentType || operation.request?.contentType || '';
  const variant = requestVariant(operation, contentType);
  const sourceBody = defaultBody(operation, contentType);
  const body = sourceBody && typeof sourceBody === 'object' ? JSON.parse(JSON.stringify(sourceBody)) : sourceBody;
  const parameters = Object.fromEntries((operation.parameters || []).map((parameter) => [parameterKey(parameter), parameterDefault(parameter)]));
  if (initialModel) {
    for (const parameter of operation.parameters || []) {
      const normalized = String(parameter.name || '').toLowerCase();
      if (parameter.in === 'path' && ['model', 'modelname', 'modename'].includes(normalized)) parameters[parameterKey(parameter)] = initialModel;
      if (parameter.in === 'path' && normalized === 'path' && operation.path.includes('*path')) parameters[parameterKey(parameter)] = `${initialModel}:generateContent`;
    }
  }
  const modelFields = topLevelFields(variant).filter((field) => /^(?:model|model_name|modelName)$/i.test(field.shortName));
  if (initialModel && body && typeof body === 'object' && !Array.isArray(body)) {
    for (const key of ['model', 'model_name', 'modelName']) {
      if (Object.prototype.hasOwnProperty.call(body, key) && !modelFields.some((field) => field.shortName === key)) body[key] = initialModel;
    }
    for (const field of modelFields) {
      if (acceptsInitialModel(field, initialModel)) body[field.shortName] = initialModel;
    }
  }
  const formValues = Object.fromEntries(topLevelFields(variant).map((field) => {
    if (field.format === 'binary') return [field.shortName, ''];
    const source = body && typeof body === 'object' && !Array.isArray(body) ? body[field.shortName] : undefined;
    const modelValue = initialModel && /^(?:model|model_name|modelName)$/i.test(field.shortName) && acceptsInitialModel(field, initialModel) ? initialModel : undefined;
    const value = modelValue ?? source ?? field.example ?? field.default ?? '';
    return [field.shortName, typeof value === 'object' ? JSON.stringify(value) : String(value)];
  }));
  return {
    contentType,
    parameters,
    bodyText: body === null ? '' : JSON.stringify(body, null, 2),
    formValues,
    files: {},
  };
}

function pathValue(parameter, draft) {
  const value = draft?.parameters?.[parameterKey(parameter)] || parameterDefault(parameter);
  return value || parameter.name.toUpperCase();
}

export function fillPath(operation, draft = createRequestDraft(operation)) {
  let output = operation.path;
  for (const parameter of (operation.parameters || []).filter((item) => item.in === 'path')) {
    output = output.replaceAll(`{${parameter.name}}`, encodeURIComponent(pathValue(parameter, draft)));
  }
  output = output.replace(/\*([A-Za-z0-9_-]+)/g, (_, name) => encodeURI(draft?.parameters?.[`path:${name}`] || name));
  return output;
}

export function resolveRequest(operation, draft = createRequestDraft(operation), apiKey = '') {
  const path = fillPath(operation, draft);
  const query = [];
  const headers = [];

  for (const parameter of operation.parameters || []) {
    const value = draft.parameters?.[parameterKey(parameter)] ?? '';
    if (value === '') continue;
    if (parameter.in === 'query') query.push({ name: parameter.name, value: String(value) });
    if (parameter.in === 'header' && !['authorization', 'x-api-key', 'content-type'].includes(parameter.name.toLowerCase())) {
      headers.push({ name: parameter.name, value: String(value) });
    }
  }

  const credential = apiKey || 'sk-your-api-key';
  if (operation.authStyle === 'anthropic') {
    headers.push({ name: 'x-api-key', value: credential });
    if (!headers.some((item) => item.name.toLowerCase() === 'anthropic-version')) {
      headers.push({ name: 'anthropic-version', value: '2023-06-01' });
    }
  } else if (operation.auth) {
    headers.push({ name: 'Authorization', value: `Bearer ${credential}` });
  }

  const method = operation.method.toUpperCase();
  const hasBody = !['GET', 'HEAD'].includes(method) && Boolean(operation.request);
  const variant = requestVariant(operation, draft.contentType);
  const contentType = draft.contentType || variant?.contentType || '';
  let jsonBody = null;
  let formEntries = [];
  let bodyError = '';

  if (hasBody && contentType.includes('multipart/form-data')) {
    formEntries = topLevelFields(variant).map((field) => ({
      name: field.shortName,
      value: draft.formValues?.[field.shortName] ?? '',
      file: draft.files?.[field.shortName] || null,
      placeholderFile: field.format === 'binary' && !draft.files?.[field.shortName] ? `${field.shortName}.bin` : '',
      field,
    })).filter((entry) => entry.file || entry.value !== '' || (entry.field.required && entry.placeholderFile));
  } else if (hasBody) {
    const parsed = parseJson(draft.bodyText || '');
    jsonBody = parsed.value;
    bodyError = parsed.error;
    if (!bodyError && contentType) headers.push({ name: 'Content-Type', value: contentType });
  }

  const queryString = new URLSearchParams(query.map((item) => [item.name, item.value])).toString();
  const relativeUrl = `${path}${queryString ? `?${queryString}` : ''}`;
  return {
    method,
    path,
    relativeUrl,
    url: `${API_BASE}${relativeUrl}`,
    headers,
    query,
    contentType,
    hasBody,
    jsonBody,
    formEntries,
    bodyError,
  };
}

function toHarRequest(resolved) {
  const request = {
    method: resolved.method,
    url: `${API_BASE}${resolved.path}`,
    httpVersion: 'HTTP/1.1',
    headers: resolved.headers,
    queryString: resolved.query,
    cookies: [],
    headersSize: -1,
    bodySize: -1,
  };
  if (resolved.hasBody && resolved.contentType.includes('multipart/form-data')) {
    request.postData = {
      mimeType: resolved.contentType,
      params: resolved.formEntries.map((entry) => entry.file || entry.placeholderFile
        ? { name: entry.name, value: '', fileName: entry.file?.name || entry.placeholderFile, contentType: entry.file?.type || 'application/octet-stream' }
        : { name: entry.name, value: String(entry.value) }),
    };
  } else if (resolved.hasBody && resolved.jsonBody !== null) {
    request.postData = { mimeType: resolved.contentType || 'application/json', text: JSON.stringify(resolved.jsonBody, null, 2) };
  }
  return request;
}

export function generateCode(operation, language = 'curl', draft = createRequestDraft(operation), apiKey = '') {
  const resolved = resolveRequest(operation, draft, apiKey);
  if (resolved.bodyError) return `请求 JSON 格式错误：${resolved.bodyError}`;
  const request = toHarRequest(resolved);
  if (language === 'python') {
    const snippet = pythonRequests.generate(request);
    return `import requests\n\nresponse = ${snippet}\nresponse.raise_for_status()\nprint(response.text)`;
  }
  if (language === 'javascript') {
    const snippet = jsFetch.generate(request);
    return `const response = await ${snippet}\n\nif (!response.ok) throw new Error(await response.text())\nconsole.log(await response.text())`;
  }
  return shellCurl.generate(request);
}

export function makeBrowserBody(resolved) {
  if (!resolved.hasBody) return undefined;
  if (resolved.contentType.includes('multipart/form-data')) {
    const form = new FormData();
    for (const entry of resolved.formEntries) {
      if (entry.file) form.append(entry.name, entry.file, entry.file.name);
      else if (!entry.placeholderFile) form.append(entry.name, entry.value);
    }
    return form;
  }
  return resolved.jsonBody === null ? undefined : JSON.stringify(resolved.jsonBody);
}

export function parseJson(value) {
  try { return { value: value.trim() ? JSON.parse(value) : null, error: '' }; }
  catch (error) { return { value: null, error: error.message }; }
}

export function searchEntries(entries, query) {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return entries
    .map((entry) => {
      const title = entry.title.toLowerCase();
      const haystack = `${entry.title} ${entry.subtitle} ${entry.keywords}`.toLowerCase();
      const matched = words.every((word) => haystack.includes(word));
      const score = matched ? words.reduce((sum, word) => sum + (title === word ? 8 : title.startsWith(word) ? 5 : title.includes(word) ? 3 : 1), 0) : 0;
      return { ...entry, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, 'zh-CN'))
    .slice(0, 20);
}

function walkValues(value, depth = 0, output = []) {
  if (depth > 5 || value === null || value === undefined) return output;
  if (typeof value === 'string') output.push(value);
  else if (Array.isArray(value)) value.forEach((item) => walkValues(item, depth + 1, output));
  else if (typeof value === 'object') Object.values(value).forEach((item) => walkValues(item, depth + 1, output));
  return output;
}

function mediaUrl(payload, extensions) {
  return walkValues(payload).find((value) => /^https?:\/\//.test(value) && extensions.test(value.split('?')[0])) || '';
}

export function normalizeResult(payload) {
  const geminiParts = payload?.candidates?.[0]?.content?.parts || [];
  const geminiImage = geminiParts.find((item) => item?.inlineData?.mimeType?.startsWith('image/') || item?.inline_data?.mime_type?.startsWith('image/'));
  const geminiAudio = geminiParts.find((item) => item?.inlineData?.mimeType?.startsWith('audio/') || item?.inline_data?.mime_type?.startsWith('audio/'));
  const responseText = Array.isArray(payload?.output)
    ? payload.output.flatMap((item) => item?.content || []).map((item) => item?.text || item?.output_text || '').filter(Boolean).join('')
    : '';
  const image = payload?.data?.[0]?.url
    || (payload?.data?.[0]?.b64_json ? `data:image/png;base64,${payload.data[0].b64_json}` : '')
    || payload?.imageUrl
    || (geminiImage ? `data:${geminiImage.inlineData?.mimeType || geminiImage.inline_data?.mime_type};base64,${geminiImage.inlineData?.data || geminiImage.inline_data?.data}` : '')
    || mediaUrl(payload, /\.(png|jpe?g|webp|gif|avif)$/i);
  const audio = payload?.audio_url
    || payload?.audioUrl
    || (geminiAudio ? `data:${geminiAudio.inlineData?.mimeType || geminiAudio.inline_data?.mime_type};base64,${geminiAudio.inlineData?.data || geminiAudio.inline_data?.data}` : '')
    || mediaUrl(payload, /\.(mp3|wav|m4a|aac|ogg|flac)$/i);
  const video = payload?.video_url || payload?.videoUrl || mediaUrl(payload, /\.(mp4|webm|mov|m3u8)$/i);
  const content = payload?.choices?.[0]?.message?.content
    ?? payload?.output_text
    ?? (responseText || undefined)
    ?? (geminiParts.length ? geminiParts.map((item) => item?.text || '').filter(Boolean).join('') : undefined)
    ?? payload?.content;
  const text = typeof content === 'string'
    ? content
    : Array.isArray(content) ? content.map((item) => item?.text || '').filter(Boolean).join('') : '';
  const sourceUsage = payload?.usage || payload?.usageMetadata || {};
  const usage = {
    ...sourceUsage,
    input_tokens: sourceUsage.input_tokens ?? sourceUsage.prompt_tokens ?? sourceUsage.promptTokenCount,
    output_tokens: sourceUsage.output_tokens ?? sourceUsage.completion_tokens ?? sourceUsage.candidatesTokenCount,
    total_tokens: sourceUsage.total_tokens ?? sourceUsage.totalTokenCount,
  };
  if (usage.total_tokens === undefined && (usage.input_tokens !== undefined || usage.output_tokens !== undefined)) {
    usage.total_tokens = Number(usage.input_tokens || 0) + Number(usage.output_tokens || 0);
  }
  return { image, audio, video, text, usage, raw: payload };
}

export function maskKey(value) {
  if (!value) return '';
  if (value.length < 12) return '••••••••';
  return `${value.slice(0, 5)}••••••••${value.slice(-4)}`;
}
